import { useEffect, useRef, useState } from 'react';

type GlobalErrorHandlerProps = {
  onSessionExpiredRedirect?: () => void;
};

export const GlobalErrorHandler = ({
  onSessionExpiredRedirect = () => window.location.reload(),
}: GlobalErrorHandlerProps) => {
  const [showModal, setShowModal] = useState(false);

  const isShowingRef = useRef(false);
  const hadValidSessionRef = useRef(false);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    const getRequestUrl = (input: RequestInfo | URL): string => {
      if (typeof input === 'string') return input;
      if (input instanceof Request) return input.url;
      return input.toString();
    };

    const normalize = (url: string) => url.toLowerCase();

    const isLoginPage = (): boolean => {
      const currentPath = window.location.pathname.toLowerCase();
      return (
        currentPath.includes('/signin') ||
        currentPath.includes('/login')
      );
    };

    const isRefreshRequest = (url: string): boolean => {
      const normalizedUrl = normalize(url);
      return normalizedUrl.includes('/refresh');
    };

    const isAuthRequest = (url: string): boolean => {
      const normalizedUrl = normalize(url);

      return (
        normalizedUrl.includes('/api/auth') ||
        normalizedUrl.includes('/auth/') ||
        normalizedUrl.includes('/signin') ||
        normalizedUrl.includes('/login') ||
        normalizedUrl.includes('/oauth') ||
        normalizedUrl.includes('/keycloak') ||
        normalizedUrl.includes('/refresh')
      );
    };

    const isSuccessfulAppRequest = (url: string, response: Response): boolean => {
      return response.ok && !isAuthRequest(url);
    };

    window.fetch = async (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> => {
      const url = getRequestUrl(input);

      try {
        const response = await originalFetch(input, init);

        // Mark that a valid session has existed
        // once at least one successful non-auth application request has been made
        if (isSuccessfulAppRequest(url, response)) {
          hadValidSessionRef.current = true;
        }

        // Only show the modal if:
        // 1) there has been a valid session before
        // 2) we are not on a login page
        // 3) the request failed with 401
        // 4) and we are not already showing the modal
        const shouldShowExpiredSessionModal =
          hadValidSessionRef.current &&
          response.status === 401 &&
          !isLoginPage() &&
          !isShowingRef.current &&
          (
            isRefreshRequest(url) || !isAuthRequest(url)
          );

        if (shouldShowExpiredSessionModal) {
          isShowingRef.current = true;
          setShowModal(true);
        }

        return response;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[GlobalErrorHandler] Fetch failed:', { url, error });
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const handleRedirect = () => {
    isShowingRef.current = false;
    setShowModal(false);
    onSessionExpiredRedirect();
  };

  const handleClose = () => {
    isShowingRef.current = false;
    setShowModal(false);
  };

  return (
    showModal ? (
      <div className="app-modal-backdrop" role="presentation">
        <div
          className="app-modal app-modal--compact"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-expired-title"
          aria-describedby="session-expired-description"
        >
          <div className="app-modal__body">
            <p className="app-modal__eyebrow">Authentication</p>
            <h2 id="session-expired-title" className="app-modal__title">
              Session expired
            </h2>
            <p id="session-expired-description" className="app-modal__description">
              Your session has expired. Continue to login to reload the app.
            </p>
          </div>
          <div className="app-modal__actions app-modal__actions--centered">
            <button
              type="button"
              className="app-button app-button--secondary"
              onClick={handleClose}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="app-button app-button--primary"
              onClick={handleRedirect}
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    ) : null
  );
};
